import { useClubEventsBetween } from "@/api/club/useClub";
import { useMyOrganizations } from "@/api/organizations/useMyOrganizations";
import { WIDE_VIEWPORT_BREAKPOINT } from "@/constants/layout";
import { routes } from "@/constants/routes";
import { useNetworkStatus } from "@/network/NetworkContext";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { CalendarAgenda } from "@/ui/components/club/CalendarAgenda";
import { CalendarMonth } from "@/ui/components/club/CalendarMonth";
import { EmptyState } from "@/ui/components/EmptyState";
import { HeaderAction } from "@/ui/components/HeaderAction";
import { Screen } from "@/ui/components/Screen";
import { ErrorState, LoadingState } from "@/ui/components/StateViews";
import { WorkTabs } from "@/ui/components/WorkTabs";
import { formatMonthYearDE } from "@/utils/isoDate";
import { useRouter } from "expo-router";
import React, { useMemo, useState } from "react";
import { Text, useWindowDimensions, View } from "react-native";

function monthRange(month: Date): { from: string; to: string } {
  const from = new Date(month.getFullYear(), month.getMonth(), 1);
  const to = new Date(month.getFullYear(), month.getMonth() + 1, 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

export default function CalendarIndexScreen() {
  const { theme } = useTheme();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isWide = width >= WIDE_VIEWPORT_BREAKPOINT;
  const { isOnline } = useNetworkStatus();
  const { organizations, memberships, loading: orgLoading } =
    useMyOrganizations();
  const org = organizations[0];
  const roles =
    memberships.find((m) => m.organization_id === org?.id)?.roles ?? [];
  const canCreateEvent = roles.some((r) =>
    ["organizer", "board", "admin"].includes(r)
  );

  const [month, setMonth] = useState(() => new Date());
  const [mode, setMode] = useState<"month" | "agenda">(
    isWide ? "month" : "agenda"
  );
  const range = useMemo(() => monthRange(month), [month]);

  const { events, loading, error, refetch } = useClubEventsBetween(
    org?.id,
    range.from,
    range.to,
    isOnline && !!org?.id
  );

  if (!isOnline) {
    return (
      <Screen title="Kalender">
        <EmptyState
          title="Offline"
          hint="Der Vereinsbereich benötigt eine Internetverbindung."
        />
      </Screen>
    );
  }

  if (orgLoading) {
    return (
      <Screen title="Kalender">
        <LoadingState />
      </Screen>
    );
  }

  if (!org) {
    return (
      <Screen title="Kalender">
        <ErrorState message="Keine aktive Vereinsmitgliedschaft." />
      </Screen>
    );
  }

  const agendaPanel = (
    <View style={{ gap: theme.space.md }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: theme.space.sm,
        }}
      >
        <HeaderAction
          label="‹ Monat"
          onPress={() =>
            setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
          }
        />
        <Text
          style={{
            color: theme.color.text,
            fontSize: theme.fontSize.small,
            fontWeight: "700",
            textTransform: "capitalize",
            flex: 1,
            textAlign: "center",
          }}
          numberOfLines={1}
        >
          {formatMonthYearDE(month.getFullYear(), month.getMonth())}
        </Text>
        <HeaderAction
          label="Monat ›"
          onPress={() =>
            setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
          }
        />
      </View>
      <CalendarAgenda
        events={events}
        onPressEvent={(ev) =>
          router.push(routes.myCalendarItem(ev.item_id) as never)
        }
      />
    </View>
  );

  const monthPanel = (
    <CalendarMonth
      month={month}
      events={events}
      onChangeMonth={setMonth}
      onSelectEvent={(ev) =>
        router.push(routes.myCalendarItem(ev.item_id) as never)
      }
    />
  );

  return (
    <Screen
      title="Kalender"
      scroll
      contentStyle={{ paddingBottom: theme.space.xxl, gap: theme.space.lg }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: theme.space.md,
        }}
      >
        <Text
          style={{
            color: theme.color.muted,
            fontSize: theme.fontSize.tiny,
            fontWeight: "600",
            flex: 1,
          }}
          numberOfLines={1}
        >
          {org.name}
        </Text>
        {canCreateEvent ? (
          <AppButton
            onPress={() =>
              router.push({
                pathname: routes.myClubNew,
                params: { kind: "event" },
              } as never)
            }
          >
            Neuer Termin
          </AppButton>
        ) : null}
      </View>

      {error ? (
        <ErrorState
          message={(error as Error).message ?? "Kalender laden fehlgeschlagen"}
          onRetry={() => void refetch()}
        />
      ) : loading && events.length === 0 ? (
        <LoadingState message="Termine laden…" />
      ) : (
        <WorkTabs
          tabs={[
            { id: "agenda", label: "Agenda", panel: agendaPanel },
            { id: "month", label: "Monat", panel: monthPanel },
          ]}
          activeId={mode}
          onSelect={(id) => setMode(id as "month" | "agenda")}
        />
      )}
    </Screen>
  );
}
